import './index.css'

import {loadDevMessages, loadErrorMessages} from '@apollo/client/dev'
import {
  browserProfilingIntegration,
  captureConsoleIntegration,
  init as initSentry,
  reactRouterV6BrowserTracingIntegration,
} from '@sentry/react'
import {StrictMode, useEffect} from 'react'
import {createRoot} from 'react-dom/client'
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom'

import App from './App'
import env from './utils/env'
import platform from './utils/platform'

if (import.meta.env.MODE === 'development') {
  const originalErrorFunction = console.error
  console.error = (...args) => {
    if (args.some(arg => CONSOLE_ERROR_FILTERS.some(f => f.test(arg)))) return null
    originalErrorFunction(...args)
  }
}
loadDevMessages()
loadErrorMessages()

if (env('SENTRY_DSN')) {
  initSentry({
    beforeSend(event) {
      try {
        const errorBag = (event.exception?.values?.reduce((bag, e) =>
          `${bag}${e.value};`, ''
        ) ?? '') + (event.message ?? '')
        if (errorBag && SENTRY_ERROR_FILTERS.some(f => f.test(errorBag))) return null
      }
      catch (e) {
        console.warn("Error on error filter", e)
      }
      return event
    },
    dist: platform,
    dsn: env('SENTRY_DSN'),
    environment: env('SENTRY_ENVIRONMENT'),
    integrations: [
      captureConsoleIntegration({levels: ['error']}),
      reactRouterV6BrowserTracingIntegration({
        createRoutesFromChildren,
        matchRoutes,
        useEffect,
        useLocation,
        useNavigationType,
      }),
      browserProfilingIntegration(),
    ],
    profilesSampleRate: 1,
    release: env('SENTRY_RELEASE'),
    // TODO: Adjust this value if we are receiving more events than we need
    //       See also: https://docs.sentry.io/platforms/javascript/performance/
    tracesSampleRate: 0.1,
  })
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>,
)

const SENTRY_ERROR_FILTERS = [
  (/networkerror when attempting to fetch resource/i),
  (/network\s*error[: ]*/i),
  (/intentional error/i),
  (/load failed/i),
  (/.*.currentObservable.query.fetchMore/i), // Known issue on WebKit
  (/Login required/i), // Access token has expired
  (/Amplitude Logger \[Error]: TypeError: Failed to fetch/i),
  // eslint-disable-next-line max-len
  (/Uniqueness violation. duplicate key value violates unique constraint \\?"merchant_users_email_address_key\\?"/i),
  (/INVALID_WHATSAPP_BUSINESS_PROFILE_SIZE/),
  // Ignore non-error promise rejections, see: https://github.com/getsentry/sentry-javascript/issues/3440
  (/Non-Error promise rejection captured/i),
  // Ignore firebase service worker errors
  (/FirebaseError: Messaging:/i),
  (/Registration failed - push service error/i),
  // Apollo error when subscriptions ends with an error
  (/socket closed/i),
  // Apollo cache warning noise: incomplete objects written into cache
  (/Missing field '%s' while writing result %o/i),
]

const CONSOLE_ERROR_FILTERS = [
  (/is deprecated/i),
]
